import type { NetworkError } from '../lib/NetworkErrorHandler';
import { NetworkErrorType } from '../lib/NetworkErrorHandler';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

interface UseNetworkMutationOptions<TData, TError, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  maxRetries?: number;
  retryDelay?: number;
}

/**
 * Enhanced mutation hook with automatic retry for network errors
 */
export function useNetworkMutation<TData, TError = Error, TVariables = void>({
  mutationFn,
  onSuccess,
  onError,
  maxRetries = 3,
  retryDelay = 1000,
}: UseNetworkMutationOptions<TData, TError, TVariables>) {
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState<TError | null>(null);

  const mutation = useMutation({
    mutationFn: async (variables: TVariables) => {
      const executeWithRetry = async (
        currentRetryCount: number,
      ): Promise<TData> => {
        try {
          const result = await mutationFn(variables);
          setRetryCount(0); // Reset retry count on success
          setLastError(null);
          return result;
        } catch (error) {
          setLastError(error as TError);

          // Check if it's a retryable network error
          if (
            error &&
            typeof error === 'object' &&
            'type' in error &&
            'isRetryable' in error
          ) {
            const networkError = error as NetworkError;

            // Don't retry offline errors
            if (networkError.type === NetworkErrorType.OFFLINE) {
              throw error;
            }

            // Auto-retry if we haven't exceeded max retries
            if (networkError.isRetryable && currentRetryCount < maxRetries) {
              const nextRetryCount = currentRetryCount + 1;
              setRetryCount(nextRetryCount);

              // Wait before retry with exponential backoff
              await new Promise((resolve) =>
                setTimeout(
                  resolve,
                  retryDelay * Math.pow(2, currentRetryCount),
                ),
              );

              // Recursive retry with updated count
              return executeWithRetry(nextRetryCount);
            }
          }

          throw error;
        }
      };

      return executeWithRetry(0);
    },
    onSuccess: (data, variables) => {
      setRetryCount(0);
      setLastError(null);
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      setLastError(error as TError);
      onError?.(error as TError, variables);
    },
  });

  const manualRetry = useCallback(() => {
    if (mutation.variables) {
      setRetryCount(0);
      mutation.mutate(mutation.variables);
    }
  }, [mutation]);

  return {
    ...mutation,
    retryCount,
    lastError,
    canRetry:
      lastError &&
      typeof lastError === 'object' &&
      'isRetryable' in lastError &&
      (lastError as unknown as NetworkError).isRetryable,
    manualRetry,
  };
}

interface UseNetworkQueryOptions<TData, TError> {
  queryKey: unknown[];
  queryFn: () => Promise<TData>;
  enabled?: boolean;
  staleTime?: number;
  retry?: number | ((failureCount: number, error: TError) => boolean);
}

/**
 * Enhanced query hook with better error handling for network issues
 */
export function useNetworkQuery<TData, TError = Error>({
  queryKey,
  queryFn,
  enabled = true,
  staleTime,
  retry,
}: UseNetworkQueryOptions<TData, TError>) {
  const customRetry =
    retry ??
    ((failureCount: number, error: unknown) => {
      // Don't retry offline errors
      if (
        error &&
        typeof error === 'object' &&
        'type' in error &&
        (error as NetworkError).type === NetworkErrorType.OFFLINE
      ) {
        return false;
      }

      // Don't retry abort errors
      if (
        error &&
        typeof error === 'object' &&
        'type' in error &&
        (error as NetworkError).type === NetworkErrorType.ABORT
      ) {
        return false;
      }

      // Retry retryable errors up to 3 times
      if (
        error &&
        typeof error === 'object' &&
        'isRetryable' in error &&
        (error as NetworkError).isRetryable
      ) {
        return failureCount < 3;
      }

      return false;
    });

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime,
    retry: customRetry,
  });
}
